import { Row, Col, Select, Alert } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { SimulationCard } from './SimulationCard';
import type { SimulationDetail } from '../../store/simulation/types';
import { SimulationMappingsTable } from './SimulationMappingsTable';
import { SimulationSellZoneMappingTable } from '../../components/simulation/simulationReporting/SimulationSellZoneMappingTable';
import { useStoreActions, useStoreState } from '../../store';
import { useEffect, useMemo, useState } from 'react';
import { useSession } from '~/hooks/useSession';
import { useQueryCreator } from '~/hooks/useQueryCreator';
import { resolveSimulationServiceVersion } from '~/store/simulation/resolveSimulationServiceVersion';
import { partnerManagementService } from '~/services';
import { ASSET_LIST_PAGE_SIZE } from '~/services/partner-management-service';
const { Option } = Select;

export interface SimulationTabProps {
  simulation: SimulationDetail;
}

export const SimulationMappingsTab = ({ simulation }: SimulationTabProps) => {
  const simulationSellRateMappings = useStoreState((state) => state.simulation.simulationSellRateMappings);
  const fetchSimulationSellRateMappings = useStoreActions(
    (actions) => actions.simulation.fetchSimulationSellRateMappings,
  );
  const sellRateMappings = useMemo(
    () => simulationSellRateMappings[simulation.simulationId] ?? [],
    [simulation, simulationSellRateMappings],
  );
  const [selectedCarrierRateId, setSelectedCarrierRateId] = useState<string>();
  const isArchived = simulation.simulationStatus === 'archived';

  // Namespace ("simulation sandbox") summary — Shipium-only, v2-only, and only when the sim was run
  // against a namespace. Fetches on demand (gated by `enabled`) since this results view, unlike the
  // setup wizard, has no NamespaceSelector to share a cache with.
  const { isShipiumView } = useSession();
  const namespaceId = simulation.namespaceId ?? undefined;
  const showNamespace =
    isShipiumView && resolveSimulationServiceVersion(simulation.simulationSetupData) === 'v2' && !!namespaceId;
  const { data: namespaces = [] } = useQuery({
    ...useQueryCreator(partnerManagementService.namespaces.search, { args: [{ count: ASSET_LIST_PAGE_SIZE }] }),
    enabled: showNamespace,
  });
  const usedNamespace = useMemo(
    () => (namespaceId ? namespaces.find((ns) => ns.namespaceId === namespaceId) : undefined),
    [namespaces, namespaceId],
  );
  useEffect(() => {
    if (sellRateMappings?.length && !selectedCarrierRateId) {
      setSelectedCarrierRateId(sellRateMappings?.[0]?.carrierRateId ?? '');
    }
  }, [selectedCarrierRateId, sellRateMappings]);

  useEffect(() => {
    // only sell_rate sims render this; the v1 endpoint 500s for v2 sims — scope the fetch.
    if (simulation && simulation.simulationType === 'sell_rate_simulation') {
      fetchSimulationSellRateMappings(simulation.simulationId);
    }
  }, [simulation, fetchSimulationSellRateMappings]);
  return (
    <>
      {showNamespace && usedNamespace && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Namespace: ${usedNamespace.name ?? namespaceId}`}
          description={usedNamespace.description || undefined}
        />
      )}
      <Row gutter={16}>
        <Col span={24}>
          <SimulationCard
            heading={'Simulation Mappings'}
            headingCount={simulation.simulationContextMappings ? simulation.simulationContextMappings?.length : 0}
            subHeading={'Simulation Mappings define the configuration to be used when running simulation shipments.'}
            archived={isArchived}
            bodyStyle={{ padding: 0, borderTop: '1px solid #eee' }}
          >
            <SimulationMappingsTable simulation={simulation} />
          </SimulationCard>
          {simulation.simulationType === 'sell_rate_simulation' && (
            <SimulationCard
              heading={'Sell Rate Zone Mappings'}
              subHeading={
                'Sell rate zone mappings enable us to cost sell rate sheets based on the selected carrier zone'
              }
              archived={isArchived}
              bodyStyle={{ padding: 0, borderTop: '1px solid #eee' }}
            >
              <Select
                value={selectedCarrierRateId}
                onChange={(e) => {
                  setSelectedCarrierRateId(e);
                }}
              >
                {sellRateMappings.map((val) => {
                  return <Option value={val.carrierRateId}>{val.partnerRateName}</Option>;
                })}
              </Select>
              {selectedCarrierRateId && (
                <SimulationSellZoneMappingTable sellCarrierRateId={selectedCarrierRateId} simulation={simulation} />
              )}
            </SimulationCard>
          )}
        </Col>
      </Row>
    </>
  );
};
